import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { companyInfo } from '../utils/dataInfo';

// Helper functions
const formatCurrency = (amount) => {
return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
}).format(amount || 0);
};

const formatNumber = (num) => {
return new Intl.NumberFormat('en-US').format(num || 0);
};

const formatDate = (date) => {
return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
});
};

const formatDateShort = (date) => {
return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
});
};

const getStatusColor = (status) => {
if (!status) return '#757575';

switch (status.toLowerCase()) {
    case 'completed': return '#4caf50';
    case 'pending': return '#ff9800';
    case 'cancelled': return '#f44336';
    case 'quotation': return '#2196f3';
    default: return '#757575';
}
};

const getPaymentMethodColor = (method) => {
if (!method) return '#757575';

switch (method.toLowerCase()) {
    case 'cash': return '#4caf50';
    case 'mobile_money': return '#2196f3';
    case 'bank': return '#673ab7';
    case 'pending': return '#ff9800';
    default: return '#757575';
}
};

const formatOrderNumber = (orderNumber) => {
if (!orderNumber) return 'N/A';
// Remove any existing dashes and format consistently
const clean = orderNumber.replace(/-/g, '');
return clean.replace(/(.{4})/g, '$1-').slice(0, -1);
};

// Generate Excel Report
export const exportClubOrdersToExcel = async (reportData, filters = {}) => {
try {
    const workbook = XLSX.utils.book_new();
    
    // Main Orders Sheet - Removed branch
    const mainHeaders = [
    'Order No.',
    'Date/Time',
    'Customer',
    'Customer Type',
    'Counter',
    'Servant',
    'Total Amount',
    'Payment',
    'Status',
    'Order Notes',
    'Payment Notes',
    'Created By',
    'Items Count'
    ];

    const mainData = reportData.orders?.map(order => [
    formatOrderNumber(order.order_number),
    formatDate(order.created_at),
    order.customer?.name || 'Walk-in Customer',
    order.customer_type?.name || 'N/A',
    order.counter?.name || 'N/A',
    order.servant?.name || 'N/A',
    order.total_amount,
    order.payment_method ? order.payment_method.toUpperCase().replace('_', ' ') : 'PENDING',
    order.order_status ? order.order_status.toUpperCase() : 'PENDING',
    order.order_notes || '',
    order.payment_notes || '',
    order.created_by?.name || 'N/A',
    order.items?.length || 0
    ]) || [];

    const mainSheetData = [mainHeaders, ...mainData];
    const mainSheet = XLSX.utils.aoa_to_sheet(mainSheetData);
    XLSX.utils.book_append_sheet(workbook, mainSheet, 'Club Orders');

    // Summary Sheet - Removed branch
    const summarySheetData = [
    ['CLUB ORDERS REPORT SUMMARY'],
    ['Generated on', new Date().toLocaleString()],
    ['Company', companyInfo.name],
    [''],
    ['Total Orders', formatNumber(reportData.summary.total_orders)],
    ['Total Sales', formatCurrency(reportData.summary.total_sales)],
    ['Total Items Sold', formatNumber(reportData.summary.total_items)],
    ['Average Order Value', formatCurrency(reportData.summary.average_order_value)],
    [''],
    ['FILTERS APPLIED'],
    ['Counter', filters.counter_name || 'All'],
    ['Servant', filters.servant_name || 'All'],
    ['Payment Method', filters.payment_method || 'All'],
    ['Order Status', filters.order_status || 'All'],
    ['Customer Type', filters.customer_type_name || 'All'],
    ['Date From', filters.date_from || 'All'],
    ['Date To', filters.date_to || 'All']
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summarySheetData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Items Sheet (if available)
    const allItems = [];
    reportData.orders?.forEach(order => {
    order.items?.forEach(item => {
        allItems.push({
        'Order No.': formatOrderNumber(order.order_number),
        'Item Name': item.stock_name || item.name || 'N/A',
        'Quantity': item.quantity,
        'Unit Price': item.price,
        'Total Amount': item.total_amount
        });
    });
    });

    if (allItems.length > 0) {
    const itemsSheet = XLSX.utils.json_to_sheet(allItems);
    XLSX.utils.book_append_sheet(workbook, itemsSheet, 'Order Items');
    }

    // Top Items Sheet
    if (reportData.summary.top_items && reportData.summary.top_items.length > 0) {
    const topItemsData = reportData.summary.top_items.map(item => ({
        'Item Name': item.stock_name,
        'Total Quantity Sold': item.total_quantity,
        'Total Sales': item.total_sales,
        'Order Count': item.order_count,
        'Average Price': item.avg_price
    }));

    const topItemsSheet = XLSX.utils.json_to_sheet(topItemsData);
    XLSX.utils.book_append_sheet(workbook, topItemsSheet, 'Top Items');
    }

    // Set column widths
    const sheets = workbook.SheetNames;
    sheets.forEach(sheetName => {
    const worksheet = workbook.Sheets[sheetName];
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const colWidths = [];
    
    for (let col = range.s.c; col <= range.e.c; col++) {
        let maxWidth = 0;
        for (let row = range.s.r; row <= range.e.r; row++) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
        if (cell && cell.v) {
            const cellWidth = String(cell.v).length;
            maxWidth = Math.max(maxWidth, cellWidth);
        }
        }
        colWidths.push({ wch: Math.min(maxWidth + 2, 50) });
    }
    
    worksheet['!cols'] = colWidths;
    });

    // Generate file name
    const fileName = `club_orders_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// Generate PDF Report - Removed Executive Summary
export const exportClubOrdersToPDF = async (reportData, filters = {}) => {
try {
    const doc = new jsPDF('landscape');
    
    // Set document properties
    doc.setProperties({
    title: 'Club Orders Report',
    subject: 'Club Sales Orders and Transaction Report',
    author: companyInfo.name,
    keywords: 'club, orders, sales, report, transactions',
    creator: companyInfo.name
    });

    // Title Page
    doc.setFontSize(24);
    doc.setTextColor(33, 150, 243);
    doc.text('CLUB ORDERS REPORT', 105, 50, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setTextColor(66, 66, 66);
    doc.text(companyInfo.name, 105, 65, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(97, 97, 97);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 105, 75, { align: 'center' });
    
    if (filters.date_from && filters.date_to) {
    doc.text(`Period: ${formatDateShort(filters.date_from)} to ${formatDateShort(filters.date_to)}`, 105, 82, { align: 'center' });
    }

    // Main Orders Table - Start directly with orders list
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('CLUB ORDERS LIST', 20, 20);

    // Prepare table data
    const tableData = reportData.orders?.map(order => [
    formatOrderNumber(order.order_number),
    formatDate(order.created_at),
    order.customer?.name?.substring(0, 15) || 'Walk-in',
    order.counter?.name?.substring(0, 10) || 'N/A',
    formatCurrency(order.total_amount),
    order.payment_method ? order.payment_method.toUpperCase().replace('_', ' ') : 'PENDING',
    order.order_status ? order.order_status.toUpperCase() : 'PENDING'
    ]) || [];

    autoTable(doc, {
    startY: 30,
    head: [['Order #', 'Date/Time', 'Customer', 'Counter', 'Amount', 'Payment', 'Status']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [33, 150, 243], textColor: 255, fontSize: 11 },
    columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 45 },
        2: { cellWidth: 35 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30, halign: 'right' },
        5: { cellWidth: 30 },
        6: { cellWidth: 30 }
    },
    didParseCell: (data) => {
        // Style the amount column
        if (data.column.index === 4 && data.cell.raw) {
        data.cell.styles.fontStyle = 'bold';
        }
        
        // Color code payment method
        if (data.column.index === 5 && data.cell.raw) {
        const color = getPaymentMethodColor(data.cell.raw.toLowerCase());
        data.cell.styles.textColor = color;
        data.cell.styles.fontStyle = 'bold';
        }
        
        // Color code status
        if (data.column.index === 6 && data.cell.raw) {
        const color = getStatusColor(data.cell.raw.toLowerCase());
        data.cell.styles.textColor = color;
        data.cell.styles.fontStyle = 'bold';
        }
    }
    });

    // Status Distribution
    if (reportData.summary.by_status && Object.keys(reportData.summary.by_status).length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('ORDER STATUS DISTRIBUTION', 20, 20);

    const statusData = Object.entries(reportData.summary.by_status).map(([status, data]) => [
        status.toUpperCase(),
        data.count,
        formatCurrency(data.total_amount || 0),
        formatCurrency(data.avg_amount || 0)
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Status', 'Count', 'Total Amount', 'Average Amount']],
        body: statusData,
        theme: 'grid',
        headStyles: { fillColor: [76, 175, 80], textColor: 255 },
        columnStyles: {
        2: { halign: 'right' },
        3: { halign: 'right' }
        },
        didParseCell: (data) => {
        if (data.column.index === 0 && data.cell.raw) {
            const color = getStatusColor(data.cell.raw.toLowerCase());
            data.cell.styles.textColor = color;
            data.cell.styles.fontStyle = 'bold';
        }
        }
    });
    }

    // Payment Method Distribution
    if (reportData.summary.by_payment_method && Object.keys(reportData.summary.by_payment_method).length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('PAYMENT METHOD DISTRIBUTION', 20, 20);

    const paymentData = Object.entries(reportData.summary.by_payment_method).map(([method, data]) => [
        method.toUpperCase().replace('_', ' '),
        data.count,
        formatCurrency(data.total_amount || 0),
        formatCurrency(data.avg_amount || 0)
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Payment Method', 'Count', 'Total Amount', 'Average Amount']],
        body: paymentData,
        theme: 'grid',
        headStyles: { fillColor: [156, 39, 176], textColor: 255 },
        columnStyles: {
        2: { halign: 'right' },
        3: { halign: 'right' }
        },
        didParseCell: (data) => {
        if (data.column.index === 0 && data.cell.raw) {
            const color = getPaymentMethodColor(data.cell.raw.toLowerCase());
            data.cell.styles.textColor = color;
            data.cell.styles.fontStyle = 'bold';
        }
        }
    });
    }

    // Top Items
    if (reportData.summary.top_items && reportData.summary.top_items.length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('TOP SELLING ITEMS', 20, 20);

    const topItemsData = reportData.summary.top_items.map(item => [
        item.stock_name?.substring(0, 20) || 'Unknown Item',
        formatNumber(item.total_quantity),
        formatCurrency(item.total_sales),
        formatCurrency(item.avg_price)
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Item Name', 'Quantity Sold', 'Total Sales', 'Avg Price']],
        body: topItemsData,
        theme: 'striped',
        headStyles: { fillColor: [255, 152, 0], textColor: 255 },
        columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' }
        }
    });
    }

    // Counter Performance
    if (reportData.summary.by_counter && Object.keys(reportData.summary.by_counter).length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('COUNTER PERFORMANCE', 20, 20);

    const counterData = Object.entries(reportData.summary.by_counter).map(([counterId, data]) => [
        data.counter_name || 'Unknown',
        data.count,
        formatCurrency(data.total_amount || 0),
        formatCurrency(data.avg_amount || 0)
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Counter', 'Order Count', 'Total Sales', 'Average Order']],
        body: counterData,
        theme: 'grid',
        headStyles: { fillColor: [0, 150, 136], textColor: 255 },
        columnStyles: {
        2: { halign: 'right' },
        3: { halign: 'right' }
        }
    });
    }

    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
    doc.text(companyInfo.name, 20, doc.internal.pageSize.height - 10);
    }

    const fileName = `club_orders_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
}
};

// Generate CSV Report - Removed branch
export const exportClubOrdersToCSV = async (reportData, filters = {}) => {
try {
    const csvLines = [];
    
    // Header
    csvLines.push('CLUB ORDERS REPORT');
    csvLines.push(`Generated on: ${new Date().toLocaleString()}`);
    csvLines.push(`Company: ${companyInfo.name}`);
    csvLines.push('');
    
    // Summary
    csvLines.push('SUMMARY');
    csvLines.push('Metric,Value');
    csvLines.push(`Total Orders,${reportData.summary.total_orders}`);
    csvLines.push(`Total Sales,${formatCurrency(reportData.summary.total_sales)}`);
    csvLines.push(`Total Items Sold,${reportData.summary.total_items}`);
    csvLines.push(`Average Order Value,${formatCurrency(reportData.summary.average_order_value)}`);
    csvLines.push('');
    
    // Main Data - Removed branch
    csvLines.push('CLUB ORDERS DETAILS');
    csvLines.push('Order No.,Date/Time,Customer,Customer Type,Counter,Servant,Total Amount,Payment,Status,Order Notes,Payment Notes,Created By,Items Count');
    
    reportData.orders?.forEach(order => {
    csvLines.push([
        `"${formatOrderNumber(order.order_number)}"`,
        `"${formatDate(order.created_at)}"`,
        `"${(order.customer?.name || 'Walk-in Customer').replace(/"/g, '""')}"`,
        `"${(order.customer_type?.name || 'N/A').replace(/"/g, '""')}"`,
        `"${(order.counter?.name || 'N/A').replace(/"/g, '""')}"`,
        `"${(order.servant?.name || 'N/A').replace(/"/g, '""')}"`,
        order.total_amount,
        `"${order.payment_method ? order.payment_method.toUpperCase().replace('_', ' ') : 'PENDING'}"`,
        `"${order.order_status ? order.order_status.toUpperCase() : 'PENDING'}"`,
        `"${(order.order_notes || '').replace(/"/g, '""')}"`,
        `"${(order.payment_notes || '').replace(/"/g, '""')}"`,
        `"${(order.created_by?.name || 'N/A').replace(/"/g, '""')}"`,
        order.items?.length || 0
    ].join(','));
    });
    
    // Items Data
    if (reportData.orders?.some(o => o.items?.length > 0)) {
    csvLines.push('');
    csvLines.push('CLUB ORDER ITEMS');
    csvLines.push('Order No.,Item Name,Quantity,Unit Price,Total Amount');
    
    reportData.orders.forEach(order => {
        order.items?.forEach(item => {
        csvLines.push([
            `"${formatOrderNumber(order.order_number)}"`,
            `"${(item.stock_name || item.name || 'N/A').replace(/"/g, '""')}"`,
            item.quantity,
            item.price,
            item.total_amount
        ].join(','));
        });
    });
    }

    // Create CSV file
    const csvContent = csvLines.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `club_orders_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};

// Quick PDF Export - Removed branch
export const exportClubOrdersQuickPDF = async (reportData) => {
try {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text('Club Orders Quick Report', 20, 20);
    
    // Summary
    doc.setFontSize(12);
    let y = 40;
    
    const quickSummary = [
    `Total Orders: ${formatNumber(reportData.summary.total_orders)}`,
    `Total Sales: ${formatCurrency(reportData.summary.total_sales)}`,
    `Total Items Sold: ${formatNumber(reportData.summary.total_items)}`,
    `Average Order Value: ${formatCurrency(reportData.summary.average_order_value)}`
    ];
    
    quickSummary.forEach(line => {
    doc.text(line, 20, y);
    y += 10;
    });
    
    y += 10;
    
    // Recent Orders
    if (reportData.orders?.length > 0) {
    doc.text('Recent Club Orders:', 20, y);
    y += 10;
    
    reportData.orders.slice(0, 10).forEach(order => {
        const line = `• ${formatOrderNumber(order.order_number)}: ${formatCurrency(order.total_amount)} (${order.order_status}) - ${order.counter?.name || 'N/A'}`;
        if (line.length > 60) {
        doc.text(line.substring(0, 60), 25, y);
        y += 7;
        doc.text(line.substring(60), 25, y);
        } else {
        doc.text(line, 25, y);
        }
        y += 7;
    });
    }
    
    const fileName = `club_orders_quick_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    return { success: true, fileName };
} catch (error) {
    console.error('Quick PDF export failed:', error);
    throw new Error('Failed to export quick PDF: ' + error.message);
}
};